'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import * as Sentry from '@sentry/react';
const { logger } = Sentry;

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Initializing...');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Don't initialize socket if we don't have the server URL
    if (!process.env.REACT_APP_IO_SERVER) {
      logger.error('REACT_APP_IO_SERVER environment variable is not set');
      setConnectionStatus('Configuration Error');
      setIsInitialized(true);
      return;
    }

    setConnectionStatus('Connecting...');
    logger.info('Initializing socket connection', {
      serverUrl: process.env.REACT_APP_IO_SERVER,
      timestamp: new Date().toISOString(),
    });

    const socketOptions = {
      path: '/socket.io',
      transports: ['websocket'],
      secure: true,
      // Add timeout and retry options
      timeout: 10000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      maxReconnectionAttempts: 5,
    };

    try {
      const newSocket = io(process.env.REACT_APP_IO_SERVER, socketOptions);

      // Connection event handlers
      newSocket.on('connect', () => {
        logger.info('Socket connected:', newSocket.id, {
          socketId: newSocket.id,
          timestamp: new Date().toISOString(),
        });
        setIsConnected(true);
        setConnectionStatus('Connected');
        setIsInitialized(true);

        // Log successful connection to Sentry
        Sentry.addBreadcrumb({
          message: 'Socket connected successfully',
          category: 'socket',
          level: 'info',
          data: {
            socketId: newSocket.id,
            timestamp: new Date().toISOString(),
          },
        });
      });

      newSocket.on('disconnect', (reason) => {
        logger.info('Socket disconnected:', {
          reason,
          socketId: newSocket.id,
          timestamp: new Date().toISOString(),
        });
        setIsConnected(false);
        setConnectionStatus('Disconnected');

        // Log disconnection to Sentry
        logger.info(`Socket disconnected: ${reason}`, 'warning', {
          tags: {
            component: 'SocketProvider',
            event_type: 'SOCKET_DISCONNECTED',
          },
          extra: {
            reason,
            socketId: newSocket.id,
            timestamp: new Date().toISOString(),
          },
        });
      });

      newSocket.on('connect_error', (error) => {
        logger.error('Socket connection error:', error, {
          serverUrl: process.env.REACT_APP_IO_SERVER,
          timestamp: new Date().toISOString(),
        });
        setIsConnected(false);
        setConnectionStatus('Connection Error');
        setIsInitialized(true);

        // Log connection error to Sentry
        Sentry.captureException(error, {
          tags: {
            component: 'SocketProvider',
            event_type: 'SOCKET_CONNECT_ERROR',
          },
          extra: {
            serverUrl: process.env.REACT_APP_IO_SERVER,
            timestamp: new Date().toISOString(),
          },
        });
      });

      newSocket.on('reconnect', (attemptNumber) => {
        logger.info('Socket reconnected after', attemptNumber, 'attempts');
        setIsConnected(true);
        setConnectionStatus('Reconnected');
      });

      newSocket.on('reconnecting', (attemptNumber) => {
        logger.info('Socket reconnecting, attempt:', attemptNumber);
        setIsConnected(false);
        setConnectionStatus(`Reconnecting... (${attemptNumber})`);
      });

      newSocket.on('reconnect_error', (error) => {
        logger.error('Socket reconnection error:', error);
        setConnectionStatus('Reconnection Failed');
      });

      newSocket.on('reconnect_failed', () => {
        logger.error('Socket reconnection failed', {
          serverUrl: process.env.REACT_APP_IO_SERVER,
          timestamp: new Date().toISOString(),
        });
        setConnectionStatus('Connection Failed');
        setIsInitialized(true);

        // Log reconnection failure to Sentry
        Sentry.captureMessage(
          'Socket reconnection failed after all attempts',
          'error',
          {
            tags: {
              component: 'SocketProvider',
              event_type: 'SOCKET_RECONNECT_FAILED',
            },
          }
        );
      });

      setSocket(newSocket);

      // Cleanup function
      return () => {
        logger.info('Cleaning up socket connection');
        newSocket.close();
      };
    } catch (error) {
      logger.error('Failed to initialize socket:', error);
      setConnectionStatus('Initialization Failed');
      setIsInitialized(true);

      Sentry.captureException(error, {
        tags: {
          component: 'SocketProvider',
          event_type: 'SOCKET_INIT_ERROR',
        },
      });
    }
  }, []);

  const value = {
    socket,
    isConnected,
    connectionStatus,
    isInitialized,
    // Helper function to check if socket is ready for use
    isSocketReady: socket && isConnected,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
